import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { AlertCircle, Trash2 } from 'lucide-react';
import type { CreateDepartmentRequest, DepartmentRow } from '@command-room/shared';
import {
  useAgents,
  useCreateDepartment,
  useDeleteDepartment,
  useDepartments,
  useUpdateDepartment,
} from '@/api/queries';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectItem } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

interface DepartmentDialogProps {
  initial?: DepartmentRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DepartmentDialog({ initial, open, onOpenChange }: DepartmentDialogProps) {
  const isEditing = !!initial;
  const { data: departments } = useDepartments();
  const { data: agents } = useAgents();

  const create = useCreateDepartment();
  const update = useUpdateDepartment();
  const del = useDeleteDepartment();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<CreateDepartmentRequest>({
    defaultValues: {
      name: initial?.name ?? '',
      goal: initial?.goal ?? '',
      responsibilities: initial?.responsibilities ?? '',
      head_agent_name: initial?.head_agent_name ?? null,
      parent_dept_id: initial?.parent_dept_id ?? null,
    },
  });

  const headAgent = watch('head_agent_name');
  const parentId = watch('parent_dept_id');

  function close(open: boolean) {
    if (!open) {
      reset();
      setConfirmDelete(false);
    }
    onOpenChange(open);
  }

  const mutation = isEditing ? update : create;
  const isPending = mutation.isPending || del.isPending;

  async function onSubmit(data: CreateDepartmentRequest) {
    if (isEditing) {
      await update.mutateAsync({ id: initial!.id, ...data });
    } else {
      await create.mutateAsync(data);
    }
    close(false);
  }

  function handleDelete() {
    del.mutate(initial!.id, { onSuccess: () => close(false) });
  }

  const otherDepts = departments?.filter((d) => d.id !== initial?.id) ?? [];

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        title={isEditing ? 'Edit department' : 'New department'}
        description={isEditing ? 'Update department details.' : 'Add a new department to your organization.'}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="dept-name">Name *</Label>
            <Input
              id="dept-name"
              {...register('name', { required: true, maxLength: 100 })}
              className={errors.name ? 'border-destructive' : ''}
              placeholder="e.g. Engineering"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="dept-goal">Goal</Label>
            <Textarea
              id="dept-goal"
              rows={2}
              {...register('goal')}
              placeholder="What this department exists to achieve"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="dept-resp">Responsibilities</Label>
            <Textarea
              id="dept-resp"
              rows={3}
              {...register('responsibilities')}
              placeholder="Key responsibilities and scope"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>Department head</Label>
              <Select
                value={headAgent ?? '__none__'}
                onValueChange={(v) => setValue('head_agent_name', v === '__none__' ? null : v)}
              >
                <SelectItem value="__none__">— none —</SelectItem>
                {agents?.map((a) => (
                  <SelectItem key={a.name} value={a.name}>
                    {a.name}
                  </SelectItem>
                ))}
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Parent department</Label>
              <Select
                value={parentId != null ? String(parentId) : '__none__'}
                onValueChange={(v) => setValue('parent_dept_id', v === '__none__' ? null : parseInt(v, 10))}
              >
                <SelectItem value="__none__">— none (root) —</SelectItem>
                {otherDepts.map((d) => (
                  <SelectItem key={d.id} value={String(d.id)}>
                    {d.name}
                  </SelectItem>
                ))}
              </Select>
            </div>
          </div>

          {(mutation.error || del.error) && (
            <p className="flex items-center gap-1 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {((mutation.error ?? del.error) as Error).message}
            </p>
          )}

          <div className="flex items-center justify-between gap-2 pt-1">
            {isEditing && (
              <div className="flex items-center gap-2">
                {confirmDelete ? (
                  <>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      disabled={isPending}
                      onClick={handleDelete}
                    >
                      {del.isPending ? 'Deleting…' : 'Confirm delete'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmDelete(false)}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setConfirmDelete(true)}
                  >
                    <Trash2 className="mr-1.5 h-4 w-4" />
                    Delete
                  </Button>
                )}
              </div>
            )}
            <div className="ml-auto flex gap-2">
              <Button type="button" variant="outline" onClick={() => close(false)} disabled={isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {mutation.isPending ? 'Saving…' : isEditing ? 'Save changes' : 'Create department'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
